import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../../libs'
import { $params } from '../../../../features/router'
import { Strategy } from '../list/types'
import { Error } from '../../../gateway/create/types'
import {
  Achievements,
  AchievementsResponse,
} from '../../achievements/list/types'

export const strategyMounted = createEvent()
export const setStrategy = createEvent<Strategy>()
export const submitUnPublishStrategy = createEvent()
export const resetImageStrategy = createEvent()

export const submitAchievementStrategy = createEvent<{
  achievementId: number
  strategyId: number
}>()

export const deleteAchievementStrategy = createEvent<{
  achievementId: number
  strategyId: number
}>()
export const submitAiMagic = createEvent<{ aiMagic: number }>()

export const achievementsStrategyMounted = createEvent()

export const setError = createEvent<Error>()

export const $strategy = restore(setStrategy, {
  account: {
    balance: 0,
    accountPlatform: '',
    mtLogin: 0,
  },
  client: {
    fullName: '',
    isTest: false,
  },
  id: 0,
  clientId: 0,
  accountId: 0,
  title: '',
  strategyPerformances: [
    {
      activeDays: 0,
      gain: '',
      grossProfit: '',
      id: 0,
      numLossOrders: 0,
      numProfitableOrders: 0,
      risk: '',
      strategyId: 0,
      sumLoss: '',
      sumProfit: '',
      winRate: '',
    },
  ],
  strategySubscriptions: [],
  strategySubscriptionPayments: [
    {
      investorAccountId: 0,
      traderCommissionUSD: 0,
      paidAt: '',
    },
  ],
  description: '',
  commission: 0,
  percentCommission: 0,
  imageName: '',
  imageLink: '',
  aiMagic: 0,
  aiAssessment: 0,
  status: '',
  publishedAt: '',
  deletedAt: '',
  startedAt: '',
  achievements: [],
  investors: 0,
  totalInvestors: 0,
  numAdditionalInvestors: 0,
})

export const $error = restore(setError, {
  message: '',
  code: 0,
})

export const getStrategy = fx<void, Strategy>({
  method: 'GET',
  url: '/strategy/',
})

export const getAchievementsStrategy = fx<void, AchievementsResponse>({
  method: 'GET',
  url: `/achievement`,
})

export const unPublishStrategy = fx<void, void>({
  method: 'PUT',
  url: '/strategy/{strategyId}/unpublish',
})

export const setAIMagic = fx<{ aiMagic: number }, void>({
  method: 'PUT',
  url: '/strategy/{strategyId}/set_ai_magic',
})

export const $achievementsStrategy = restore<Achievements[]>(
  getAchievementsStrategy.doneData.map((a) => a.body.items),
  [],
)

export const addAchievement = fx<
  {
    achievementId: number
    strategyId: number
  },
  void
>({
  method: 'POST',
  url: '/strategy/achievement',
})

export const deleteAchievement = fx<
  {
    achievementId: number
    strategyId: number
  },
  void
>({
  method: 'DELETE',
  url: '/strategy/achievement',
})

sample({
  clock: [strategyMounted, deleteAchievement.doneData, setAIMagic.doneData],
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.strategyID
    }
    return {
      query: {
        populate: JSON.stringify([
          'Strategy.account',
          'Strategy.strategyPerformances',
          'Strategy.achievements',
          'Strategy.strategySubscriptions',
        ]),
      },
      url: `/strategy/${id}`,
    }
  },
  target: getStrategy,
})

sample({
  clock: achievementsStrategyMounted,
  fn: () => {
    return {
      url: `/achievement?where{"deletedAt":"null"}`,
    }
  },
  target: getAchievementsStrategy,
})

sample({
  clock: [getStrategy.doneData],
  fn: (doneData) => doneData.body,
  target: [setStrategy],
})

sample({
  clock: submitUnPublishStrategy,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.strategyID
    }
    return {
      url: `/strategy/${id}/unpublish`,
    }
  },
  target: unPublishStrategy,
})

sample({
  clock: resetImageStrategy,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.strategyID
    }
    return {
      url: `/strategy/${id}/reset_image`,
    }
  },
  target: unPublishStrategy,
})

sample({
  clock: submitAiMagic,
  source: $params,
  fn: (params, { aiMagic }) => {
    let id
    if (params) {
      id = params.strategyID
    }
    return {
      url: `/strategy/${id}/set_ai_magic`,
      body: {
        aiMagic,
      },
    }
  },
  target: setAIMagic,
})

sample({
  clock: [
    unPublishStrategy.failData,
    setAIMagic.failData,
    deleteAchievement.failData,
    addAchievement.failData,
  ],
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setError],
})

sample({
  clock: submitAchievementStrategy,
  fn: ({ achievementId, strategyId }) => {
    return {
      url: `/strategy/achievement`,
      body: {
        achievementId,
        strategyId,
      },
    }
  },
  target: addAchievement,
})

sample({
  clock: deleteAchievementStrategy,
  fn: ({ achievementId, strategyId }) => {
    return {
      url: `/strategy/achievement`,
      body: {
        achievementId,
        strategyId,
      },
    }
  },
  target: deleteAchievement,
})
